import { FC, useCallback, useEffect, useRef } from 'react';

import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';

import { GOOGLE_MAPS } from '../../constants';
import { RetailsTestID } from '../../test-ids';
import { MapMarkers } from '../MapMarkers';
import { useRetailsContext } from '../RetailsContext';
import { MapContainer, MapSkeleton } from './Map.styles';

export const RetailsMap: FC = () => {
    const mapRef = useRef<google.maps.Map | null>(null);
    const mountRef = useRef(false);

    const {
        data: { apiKey },
        zoom,
        position,
        filteredCities,
        selectCity,
        selectSalon,
        animatedSalonMarkerId,
        selectedCity,
        selectedSalon,
        isMobileMapVisible,
        setZoom,
        setPosition,
        selection,
    } = useRetailsContext();

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: apiKey,
        version: GOOGLE_MAPS.version,
        language: GOOGLE_MAPS.language,
        libraries: GOOGLE_MAPS.libraries,
        nonce: GOOGLE_MAPS.nonce,
    });

    const handleMapLoad = useCallback(
        (map: google.maps.Map) => {
            mapRef.current = map;

            if (!selection.cityId) {
                mapRef.current.fitBounds(GOOGLE_MAPS.initialBounds);
            }
        },
        [selection.cityId],
    );

    const handlePositionChange = useCallback(() => {
        if (mapRef.current) {
            const center = mapRef.current.getCenter();
            const newPosition = { lat: center.lat(), lng: center.lng() };

            if (newPosition.lat !== position.lat || newPosition.lng !== position.lng) {
                setPosition(newPosition);
            }
        }
    }, [position, setPosition]);

    const handleZoomChange = useCallback(() => {
        if (mapRef.current) {
            const newZoom = mapRef.current.getZoom();

            if (newZoom !== zoom) {
                setZoom(newZoom);
            }
        }
    }, [setZoom, zoom]);

    useEffect(() => {
        if (isLoaded) {
            if (!selection?.cityId && !selection?.salonId && mountRef.current) {
                mapRef.current.fitBounds(GOOGLE_MAPS.initialBounds);
            }

            mountRef.current = true;
        }
    }, [isLoaded, selection]);

    const isMobileHidden = !isMobileMapVisible && !!selectedCity && !selectedSalon;

    if (!isLoaded) {
        return (
            <MapContainer>
                <MapSkeleton />
            </MapContainer>
        );
    }

    return (
        <MapContainer $isMobileHidden={isMobileHidden} data-test={RetailsTestID.MAP}>
            <GoogleMap
                onLoad={handleMapLoad}
                zoom={zoom}
                center={position}
                onZoomChanged={handleZoomChange}
                onDragEnd={handlePositionChange}
                mapContainerStyle={{ height: '100%' }}
                options={{
                    streetViewControl: false,
                    scaleControl: false,
                    mapTypeControl: false,
                    panControl: false,
                    rotateControl: false,
                    fullscreenControl: false,
                }}
            >
                <MapMarkers
                    cities={filteredCities}
                    zoom={zoom}
                    animatedSalonId={animatedSalonMarkerId}
                    onWorldMarkerClick={() => selectCity()}
                    onCityMarkerClick={selectCity}
                    onSalonMarkerClick={selectSalon}
                    selectedCity={selectedCity}
                />
            </GoogleMap>
        </MapContainer>
    );
};
